import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "react-query";
import { toast } from "react-toastify";
import api from "../../../utils/api";

const EMPTY_MEMBER = { name: "", age: "" };

export default function BookAppointment() {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedChamber, setSelectedChamber] = useState("");
  const [forSelf, setForSelf] = useState(true);
  const [member, setMember] = useState(EMPTY_MEMBER);
  const [paymentMode, setPaymentMode] = useState("offline"); // ✅ default offline

  const { data: doctorData } = useQuery(["doctor-detail", doctorId], () =>
    api.get(`/patient/doctors/${doctorId}`)
  );

  const { data: slotsData } = useQuery(
    ["doctor-slots", doctorId, selectedChamber],
    () =>
      api.get(`/patient/doctors/${doctorId}/slots?chamberId=${selectedChamber}`),
    { enabled: !!selectedChamber }
  );

  const { data: profileData } = useQuery("patient-profile", () =>
    api.get("/patient/profile")
  );
  const patientName = profileData?.profile?.name ?? "";

  const bookMutation = useMutation(
    () =>
      api.post(`/patient/appointments`, {
        doctorId,
        chamberId: selectedChamber,
        slotId: selectedSlot,
        forMember: !forSelf ? member : null,
        paymentMode,
      }),
    {
      onSuccess: (res) => {
        toast.success("Appointment booked!");
        if (paymentMode === "online" && res.paymentUrl) {
          window.location.href = res.paymentUrl;
        } else {
          navigate("/patient/booking-history");
        }
      },
      onError: (err) => toast.error(err.message),
    }
  );

  const doctor = doctorData?.doctor;

  return (
    <div className="max-w-2xl space-y-5">
      {/* Doctor Summary */}
      {doctor && (
        <div className="card flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-3xl">
            👨‍⚕️
          </div>
          <div>
            <h2 className="font-display font-semibold text-slate-800 text-lg">
              Dr. {doctor.name}
            </h2>
            <p className="text-primary-600 text-sm">{doctor.specialization}</p>
            <p className="text-slate-500 text-xs">{doctor.degree}</p>
          </div>
        </div>
      )}

      {/* Select Chamber */}
      <div className="card space-y-3">
        <h3 className="font-display font-semibold text-slate-800">
          1. Select Chamber
        </h3>
        <select
          value={selectedChamber}
          onChange={(e) => { setSelectedChamber(e.target.value); setSelectedSlot(null); }}
          className="input-field"
        >
          <option value="">-- Choose a chamber --</option>
          {(doctor?.chambers || []).map((ch) => (
            <option key={ch.id} value={ch.id}>
              {ch.location} | {ch.days} | ₹{ch.fees}
            </option>
          ))}
        </select>
      </div>

      {/* Select Slot */}
      {selectedChamber && (
        <div className="card space-y-3">
          <h3 className="font-display font-semibold text-slate-800">
            2. Choose a Slot
          </h3>
          <div className="flex flex-wrap gap-2">
            {/* ✅ Show Slot 1, Slot 2... instead of time */}
            {(slotsData?.slots || []).map((slot, index) => (
              <button
                key={slot.id}
                disabled={!slot.available}
                onClick={() => setSelectedSlot(slot.id)}
                className={`px-4 py-2 rounded-lg text-sm border transition-colors ${
                  selectedSlot === slot.id
                    ? "bg-primary-600 border-primary-600 text-white"
                    : slot.available
                    ? "bg-white border-slate-200 hover:bg-slate-50"
                    : "bg-slate-100 border-slate-100 text-slate-400 cursor-not-allowed"
                }`}
              >
                Slot {index + 1}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Booking For */}
      <div className="card space-y-3">
        <h3 className="font-display font-semibold text-slate-800">
          3. Booking For
        </h3>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" checked={forSelf} onChange={() => setForSelf(true)} />
            <span className="text-sm">Myself</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" checked={!forSelf} onChange={() => setForSelf(false)} />
            <span className="text-sm">Someone else</span>
          </label>
        </div>

        {forSelf && patientName && (
          <div className="flex items-center gap-2 px-3 py-2 bg-primary-50 border border-primary-100 rounded-lg w-fit">
            <span className="text-lg">👤</span>
            <span className="text-sm font-medium text-primary-700">{patientName}</span>
          </div>
        )}

        {!forSelf && (
          <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
              placeholder="Name"
              value={member.name}
              onChange={(e) => setMember((m) => ({ ...m, name: e.target.value }))}
              className="input-field"
            />
            <input
              type="number"
              placeholder="Age"
              value={member.age}
              onChange={(e) => setMember((m) => ({ ...m, age: e.target.value }))}
              className="input-field"
            />
          </div>
        )}
      </div>

      {/* Payment Mode */}
      <div className="card space-y-3">
        <h3 className="font-display font-semibold text-slate-800">
          4. Payment to Doctor
        </h3>
        <div className="flex gap-4">
          {/* ✅ Online disabled, offline always selected */}
          <label className="flex items-center gap-2 cursor-not-allowed opacity-40">
            <input type="radio" checked={false} disabled />
            <span className="text-sm">Online</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" checked={true} readOnly />
            <span className="text-sm">Offline</span>
          </label>
        </div>
      </div>

      {/* Confirm */}
      <button
        onClick={() => bookMutation.mutate()}
        disabled={!selectedSlot || bookMutation.isLoading}
        className="btn-primary w-full text-base py-3"
      >
        {bookMutation.isLoading ? "Booking…" : "Confirm Appointment"}
      </button>
    </div>
  );
}